/* eslint-disable @typescript-eslint/member-ordering */
import { AsyncPipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
  MatLegacySelectModule,
} from '@angular/material/legacy-select';
import { RxState } from '@rx-angular/state';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Pagination } from '@core/interfaces/ui/pagination.interface';
import { APP_FORM_FIELD_PROVIDER } from '@core/providers/form-field.provider';
import { FormErrorComponent } from '@shared/components/form-error/form-error.component';
import { OptionAsTableComponent } from '@shared/components/option-as-table/option-as-table.component';
import { MatSelectInfiniteScrollDirective } from '@shared/directives/mat-select-infinite-scroll.directive';
import { ScrollLoadingOverlayDirective } from '@shared/directives/scroll-loading-overlay.directive';
import { ThBaseFormFieldControlElement } from '@shared/form/base-form-field-control-element.directive';
import { GridColumnModel } from '@shared/modules/grid/models/grid/grid-column.model';
import { LoaderModule } from '@shared/modules/loader/loader.module';

interface ISelectState<T> {
  data: T[];
  selectedItems: T[];
  showSelectedOutOfListItem: boolean;
}

type TOptionTemplateType = 'default' | 'tree' | 'table';

@Component({
  selector: 'app-form-select',
  standalone: true,
  templateUrl: './form-select.component.html',
  styleUrls: ['./form-select.component.scss'],
  providers: [RxState],
  imports: [
    NgIf,
    LoaderModule,
    MatLegacySelectModule,
    MatSelectInfiniteScrollDirective,
    ScrollLoadingOverlayDirective,
    MatLegacyInputModule,
    MatIconModule,
    NgForOf,
    AsyncPipe,
    NgTemplateOutlet,
    NgSwitchCase,
    NgClass,
    FormErrorComponent,
    NgSwitch,
    FormsModule,
    ReactiveFormsModule,
    OptionAsTableComponent,
    NgxTrimDirectiveModule,
  ],
})
export class FormSelectComponent<V, T> extends ThBaseFormFieldControlElement<V> implements OnInit {
  @Input() templateType: TOptionTemplateType = 'default';

  @Input() optionColumns: GridColumnModel[];

  @Input() pagination: Pagination;

  @Input() emptyLabelPrefix = 'Choose';

  @Input() optionsLabel: string;

  @Input() showEmptyOption = true;

  @Input() multiple = false;

  @Input() readonly: boolean;

  @Input() titleField = 'name';

  @Input() subTitleField = null;

  @Input() valueField = 'id';

  @Input() loading = false;

  @Input() showSearch = false;

  @Input() loadOnScroll = false;

  @Input() showOutOfScopeValue = false;

  @Input() enableDiscardingSearchResultsOnBlur = true;

  @Input() set runClearSearch(value: boolean) {
    if (value) {
      this.clearSearch();
    }
  }

  @Input() set data(data: T[]) {
    this.state.set({
      data,
    });
  }

  @Input() set selectedItem(item: T) {
    this.state.set({
      selectedItems: [item],
    });
  }

  @Output() itemSelected = new EventEmitter<T>();

  @Output() selectSearch = new EventEmitter<string>();

  @Output() selectLoadNextPage = new EventEmitter<number>();

  data$ = this.state.select('data');
  dataLength$ = this.state.select('data').pipe(map(data => (data ? data.length : 0)));
  outOfScopeValue$ = this.state.select('data').pipe(
    filter(data => !!data && data.length > 0),
    map(data => {
      const availableValues = data.map(dataItem => dataItem[this.valueField]);
      if (availableValues.includes(this.formControl.value)) {
        return null;
      } else {
        return this.formControl.value;
      }
    }),
  );

  get selectedItemsLabel(): string {
    const items = this.state.get('selectedItems') || [];
    if (!items.length) {
      return '';
    }

    const rawField = this.optionColumns?.[0]?.fieldName;
    if (!rawField) {
      return '';
    }

    const field = Array.isArray(rawField) ? rawField[0] : rawField;

    return items.map(i => i[field]).join(', ');
  }

  selectedItems$ = this.state.select('selectedItems');
  showSelectedOutOfListItem$ = this.state.select('showSelectedOutOfListItem');

  searchString = '';

  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('matSelectInstance') matSelectInstance: MatSelect;

  constructor(
    private readonly state: RxState<ISelectState<T>>,
    @Inject(APP_FORM_FIELD_PROVIDER) public readonly formFieldProvider: {showErrors: boolean},
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.state.connect('selectedItems', this.formControl.valueChanges,
      (oldState, value) => {
        const values = Array.isArray(value) ? value : [value];
        return (oldState.data || []).filter(item =>
          values.includes(item[this.valueField]),
        );
      },
    );
    this.state.connect(
      'showSelectedOutOfListItem',
      combineLatest<[T[], T[]]>([
        this.state.select('data'),
        this.state.select('selectedItems'),
      ]).pipe(
        map(([data, selectedItems]) => {
          if (!selectedItems || selectedItems.length === 0) {
            return false;
          }
          const availableValues = (data || []).map(item => item[this.valueField]);

          return selectedItems.some(
            selected =>
              !!selected
              && !availableValues.includes(selected[this.valueField]),
          );
        }),
      ),
    );
  }

  /**
   * Emits a whole item object on select
   */
  itemSelect($event: MatSelectChange): void {
    const item = this._findItem($event.value);
    this.itemSelected.emit(item);
  }

  /**
   * Closes the dropdown on click outside
   * Note: Fixes issue with multiple dropdowns opened on mobile
   */
  onClickedOutside($event: unknown, matSelect: MatSelect): void {
    matSelect.close();
  }

  searchEvent(event: Event): void {
    event.stopPropagation();
    const searchString = this.searchString.trim();
    this.search(searchString);
    this.searchString = searchString;
  }

  clearSearch(): void {
    this.searchString = '';
    this.search(this.searchString);
  }

  search(searchString: string): void {
    if (this.matSelectInstance && this.matSelectInstance.panel) {
      this.matSelectInstance.panel.nativeElement.scrollTop = 0;
    }
    this.selectSearch.emit(searchString);
  }

  onScrollDown(): void {
    this.loadNextPage();
  }

  loadNextPage(): void {
    if (this.pagination.page < this.pagination.pageCount) {
      this.selectLoadNextPage.emit(++this.pagination.page);
    }
  }

  /**
   * Finds item in data list with a corresponding value
   * @param value Lookup value
   */
  private _findItem(value: string | string[]): T {
    const data = this.state.get('data') || [];
    const actualValue = Array.isArray(value) ? value[0] : value;
    return data.find(selectItem => selectItem[this.valueField] === actualValue);
  }

  openedHandler(isOpened: boolean): void {
    if (this.searchString.length && !isOpened && this.enableDiscardingSearchResultsOnBlur) {
      this.clearSearch();
    }
  }

  modelChanged(data: string): void {
    if (!data.length && this.enableDiscardingSearchResultsOnBlur) {
      this.clearSearch();
    }
  }
}
